import { LayoutGraph } from './types';

/**
 * Finds all nodes that should be included in the layout:
 * - If no nodes are selected, returns all nodes
 * - Otherwise, returns selected nodes plus all nodes "between" them
 *
 * A node is "between" selected nodes if it lies on a path connecting them,
 * meaning it is reachable from at least one selected node AND can reach
 * at least one selected node.
 */
export function findEnclosedNodes(graph: LayoutGraph, selectedNodeIds: number[]): number[] {
    // If no selection, return all nodes
    if (selectedNodeIds.length === 0) {
        return graph.nodes.map(node => node.id);
    }

    const selectedSet = new Set(selectedNodeIds);

    // Build adjacency maps for forward and backward traversal
    const forwardMap = new Map<number, number[]>(); // nodeId -> [downstream nodes]
    const backwardMap = new Map<number, number[]>(); // nodeId -> [upstream nodes]

    for (const conn of graph.connections) {
        // Forward: sending -> receiving
        if (!forwardMap.has(conn.sendingNodeId)) {
            forwardMap.set(conn.sendingNodeId, []);
        }
        forwardMap.get(conn.sendingNodeId)!.push(conn.receivingNodeId);

        // Backward: receiving -> sending
        if (!backwardMap.has(conn.receivingNodeId)) {
            backwardMap.set(conn.receivingNodeId, []);
        }
        backwardMap.get(conn.receivingNodeId)!.push(conn.sendingNodeId);
    }

    // Find all nodes reachable from selected nodes (forward)
    const reachableFromSelected = new Set<number>();
    const forwardQueue = [...selectedNodeIds];
    const forwardVisited = new Set<number>(selectedNodeIds);

    while (forwardQueue.length > 0) {
        const current = forwardQueue.shift()!;
        reachableFromSelected.add(current);

        const neighbors = forwardMap.get(current) || [];
        for (const neighbor of neighbors) {
            if (!forwardVisited.has(neighbor)) {
                forwardVisited.add(neighbor);
                forwardQueue.push(neighbor);
            }
        }
    }

    // Find all nodes that can reach selected nodes (backward)
    const canReachSelected = new Set<number>();
    const backwardQueue = [...selectedNodeIds];
    const backwardVisited = new Set<number>(selectedNodeIds);

    while (backwardQueue.length > 0) {
        const current = backwardQueue.shift()!;
        canReachSelected.add(current);

        const neighbors = backwardMap.get(current) || [];
        for (const neighbor of neighbors) {
            if (!backwardVisited.has(neighbor)) {
                backwardVisited.add(neighbor);
                backwardQueue.push(neighbor);
            }
        }
    }

    // Return nodes that are either:
    // 1. Selected, OR
    // 2. Both reachable from selected AND can reach selected (between them)
    const result = new Set<number>();

    for (const nodeId of graph.nodes.map(n => n.id)) {
        if (selectedSet.has(nodeId) || (reachableFromSelected.has(nodeId) && canReachSelected.has(nodeId))) {
            result.add(nodeId);
        }
    }

    return Array.from(result);
}
